import classNames from "classnames";
import { useEffect, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { IconLoading } from "@icons/index";
import {
  GeneratedReferenceVideoCard,
  ReferenceVideoThumbnailCard,
  type ReferenceVideoLibraryItem,
} from "@pages/videoRemake/ReferenceVideoCard";

export type LibrarySubTab = "generated" | "uploaded" | "saved";

export interface ReferenceVideoLibraryPanelProps {
  subTab: LibrarySubTab;
  onSubTabChange: (tab: LibrarySubTab) => void;
  videos: ReferenceVideoLibraryItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  scrollTop: number;
  sentinelRef: (node: HTMLElement | null) => void;
  onScrollTopChange: (tab: LibrarySubTab, scrollTop: number) => void;
  onRetry: () => void;
  onSelect: (video: ReferenceVideoLibraryItem) => void;
}

export const ReferenceVideoLibraryPanel = ({
  subTab,
  onSubTabChange,
  videos,
  loading,
  loadingMore,
  hasMore,
  error,
  scrollTop,
  sentinelRef,
  onScrollTopChange,
  onRetry,
  onSelect,
}: ReferenceVideoLibraryPanelProps) => {
  const { t } = useTranslation();
  const LIBRARY_SUB_TABS: { key: LibrarySubTab; label: string }[] = useMemo(
    () => [
      { key: "generated", label: t("Generated") },
      { key: "uploaded", label: t("Uploaded") },
      { key: "saved", label: t("Saved") },
    ],
    [t],
  );
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = scrollTop;
  }, [scrollTop, subTab]);

  return (
    <>
      {/* 二级 tab */}
      <div className="flex flex-col items-start px-[8px] py-[12px]">
        <div className="flex items-center gap-[32px] h-[32px]">
          {LIBRARY_SUB_TABS.map((tab) => {
            const isActive = tab.key === subTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onSubTabChange(tab.key)}
                className={classNames(
                  "flex items-center justify-center py-[4px] cursor-pointer transition-colors",
                  isActive ? "text-kds-fg" : "text-kds-fg-subtle hover:text-kds-fg-soft",
                )}
              >
                <span className="text-[16px] leading-[24px] font-semibold whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 列表 */}
      <div
        ref={listRef}
        className="flex max-h-[480px] flex-col items-center overflow-y-auto overflow-x-hidden pb-[24px]"
        onScroll={(event) => onScrollTopChange(subTab, event.currentTarget.scrollTop)}
      >
        {loading ? (
          <div className="flex h-[270px] w-full items-center justify-center text-kds-fg-soft">
            <IconLoading className="size-[24px] animate-spin" />
          </div>
        ) : error ? (
          <div className="flex h-[270px] w-full flex-col items-center justify-center gap-[12px] text-[14px] text-kds-fg-soft">
            <Trans>Load failed.</Trans>
            <button
              type="button"
              onClick={onRetry}
              className="rounded-[8px] border border-kds-border px-[12px] py-[6px] text-kds-fg transition-colors hover:border-kds-accent"
            >
              <Trans>Retry</Trans>
            </button>
          </div>
        ) : videos.length > 0 ? (
          <div className="grid w-full grid-cols-4 gap-[16px]">
            {videos.map((video) => (
              <ReferenceVideoCard key={video.id} video={video} onClick={() => onSelect(video)} />
            ))}
            {hasMore && <div ref={sentinelRef} className="col-span-4 h-px w-full" />}
            {loadingMore && (
              <div className="col-span-4 flex h-[32px] w-full items-center justify-center text-kds-fg-soft">
                <IconLoading className="size-[18px] animate-spin" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-[270px] text-kds-fg-soft text-[14px]">
            <Trans>Nothing here yet.</Trans>
          </div>
        )}
      </div>
    </>
  );
};

interface ReferenceVideoCardProps {
  video: ReferenceVideoLibraryItem;
  onClick: () => void;
}

function ReferenceVideoCard({ video, onClick }: ReferenceVideoCardProps) {
  if (video.variant === "generated") {
    return <GeneratedReferenceVideoCard video={video} onClick={onClick} />;
  }

  return <ReferenceVideoThumbnailCard video={video} onClick={onClick} />;
}
